import currency from 'currency.js'

const BRL = (value: number) => currency(value)

const WEIGHT_TO_GRAM: Record<string, number> = {
  Kilo: 1000,
  Grama: 1,
  Miligrama: 0.001
}

const VOLUME_TO_ML: Record<string, number> = {
  Litro: 1000,
  Mililitro: 1
}

const weightMeasures = Object.keys(WEIGHT_TO_GRAM)
const volumeMeasures = Object.keys(VOLUME_TO_ML)

function parseQuantity(quantity: string | undefined): number | null {
  if (quantity === undefined || quantity === '') {
    return null
  }
  const normalized = String(quantity).replace(',', '.')
  const value = Number(normalized)
  if (Number.isNaN(value)) {
    return null
  }
  return value
}

function quantityInPriceUnits(
  quantity: number,
  recipeMeasure: string | undefined,
  priceMeasure: string,
  unitMeasure: INGREDIENT_TYPES
): number | null {
  if (unitMeasure === 'weight') {
    const recipeM =
      recipeMeasure && weightMeasures.includes(recipeMeasure)
        ? recipeMeasure
        : 'Grama'
    if (!weightMeasures.includes(priceMeasure)) {
      return null
    }
    const grams = quantity * WEIGHT_TO_GRAM[recipeM]
    return grams / WEIGHT_TO_GRAM[priceMeasure]
  }

  if (unitMeasure === 'volume') {
    const recipeM =
      recipeMeasure && volumeMeasures.includes(recipeMeasure)
        ? recipeMeasure
        : 'Mililitro'
    if (!volumeMeasures.includes(priceMeasure)) {
      return null
    }
    const ml = quantity * VOLUME_TO_ML[recipeM]
    return ml / VOLUME_TO_ML[priceMeasure]
  }

  if (unitMeasure === 'unit') {
    if (priceMeasure !== 'Unidade') {
      return null
    }
    return quantity
  }

  if (unitMeasure === 'meter') {
    if (priceMeasure !== 'Metro') {
      return null
    }
    return quantity
  }

  return null
}

export function formatIngredientCatalogPrice(
  ingredient?: Partial<IIngredient>
): string {
  if (!ingredient?.price || !ingredient?.price_measure) {
    return '-'
  }
  return `${new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(+ingredient.price)} / ${ingredient.price_measure}`
}

export function calculateIngredientLineCost(
  dishIngredient: IDishIngredient
): number | null {
  const ingredient = dishIngredient.ingredient
  if (
    !ingredient?.price ||
    !ingredient.price_measure ||
    !ingredient.unit_measure
  ) {
    return null
  }

  const quantity = parseQuantity(dishIngredient.quantity)
  if (quantity === null) {
    return null
  }

  const units = quantityInPriceUnits(
    quantity,
    dishIngredient.measure,
    ingredient.price_measure,
    ingredient.unit_measure
  )

  if (units === null) {
    return null
  }

  return BRL(+ingredient.price).multiply(units).value
}

function collectDishIngredients(dish: IDishData | null): IDishIngredient[] {
  if (!dish) {
    return []
  }

  const main =
    dish.ingredients?.filter(ingredient => ingredient.ingredient) ?? []

  const fromCompositions =
    dish.compositions?.flatMap(
      composition =>
        composition.dishComposition?.ingredients?.filter(
          ingredient => ingredient.ingredient
        ) ?? []
    ) ?? []

  return [...main, ...fromCompositions]
}

export function calculateDishIngredientsTotalCost(
  dish: IDishData | null
): number | null {
  const lines = collectDishIngredients(dish)
  let total = 0
  let hasAny = false

  for (const line of lines) {
    const cost = calculateIngredientLineCost(line)
    if (cost !== null) {
      total = BRL(total).add(cost).value
      hasAny = true
    }
  }

  return hasAny ? total : null
}

export function calculatePricePerPortion(dish: IDishData | null): string {
  const total = calculateDishIngredientsTotalCost(dish)
  if (total === null) {
    return '-'
  }

  const portion = parseQuantity(dish?.portion)
  if (portion === null || portion === 0) {
    return '-'
  }

  const perPortion = BRL(total).divide(portion).value

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(perPortion)
}
